import React, { useCallback, useRef, useState } from 'react';
import { uploadMedia } from '../apiMedia';
import { partitionFiles, formatBytes, normalizeTags } from '../mediaHelpers';
import TagInput from './TagInput';
import Alert from '../ui/Alert';

/**
 * The one upload control, shared by the library page's Upload modal and the
 * image picker's Upload tab: a drag-and-drop zone + file input (multi-file),
 * shared metadata fields, and per-file progress.
 *
 *   onUploaded(mediaDoc)  fires once per successful file (the picker selects it)
 *   onFinished(summary)   fires when the whole batch settles
 *                         ({ uploaded: n, failed: n, items: [...] })
 *
 * Fields are shared across the batch deliberately: a single name/alt/tag set for
 * a group of related images is the common editorial case, and every field stays
 * editable afterwards on the detail page.
 */
export default function UploadPanel({ onUploaded, onFinished, autoFocus = false }) {
  const [queue, setQueue] = useState([]); // [{ key, file, progress, status, error }]
  const [fields, setFields] = useState({ name: '', description: '', tags: [], source: '', contributor_name: '', editorial_only: false });
  const [rejected, setRejected] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const keyRef = useRef(0);

  const addFiles = useCallback((picked) => {
    const { accepted, rejected: bad } = partitionFiles(picked);
    setRejected(bad);
    if (!accepted.length) return;
    setQueue((prev) => [
      ...prev,
      ...accepted.map((file) => ({ key: `f${++keyRef.current}`, file, progress: 0, status: 'queued', error: null })),
    ]);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer && e.dataTransfer.files);
  };

  const setItem = (key, patch) =>
    setQueue((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));

  const removeItem = (key) => setQueue((prev) => prev.filter((item) => item.key !== key));

  const startUpload = async () => {
    const pending = queue.filter((item) => item.status === 'queued' || item.status === 'error');
    if (!pending.length || uploading) return;
    setUploading(true);

    const payload = { ...fields, tags: normalizeTags(fields.tags) };
    const items = [];
    let failed = 0;

    // Sequential on purpose: a browser drag of 20 photos would otherwise open 20
    // concurrent multipart POSTs against one App Engine instance.
    for (const item of pending) {
      setItem(item.key, { status: 'uploading', progress: 0, error: null });
      try {
        const media = await uploadMedia(item.file, payload, (pct) => setItem(item.key, { progress: pct }));
        setItem(item.key, { status: 'done', progress: 100 });
        items.push(media);
        if (onUploaded) onUploaded(media);
      } catch (err) {
        failed += 1;
        const detail = err.response?.data?.detail;
        setItem(item.key, {
          status: 'error',
          error: typeof detail === 'string' ? detail : (err.message || 'Upload failed'),
        });
      }
    }

    setUploading(false);
    if (onFinished) onFinished({ uploaded: items.length, failed, items });
  };

  const pendingCount = queue.filter((item) => item.status === 'queued' || item.status === 'error').length;

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current && inputRef.current.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (inputRef.current) inputRef.current.click();
          }
        }}
        style={{
          border: `2px dashed ${dragging ? 'var(--color-primary)' : 'var(--color-border)'}`,
          borderRadius: 'var(--radius)',
          background: dragging ? 'var(--color-bg)' : 'transparent',
          padding: '24px',
          textAlign: 'center',
          cursor: 'pointer',
          marginBottom: '16px',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Drop images here</div>
        <div className="muted small">or click to choose files — JPEG, PNG, WebP or GIF, up to 20 MB each</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif"
          autoFocus={autoFocus}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
          style={{ display: 'none' }}
        />
      </div>

      {rejected.length > 0 && (
        <Alert type="warning" onDismiss={() => setRejected([])}>
          {rejected.map((r) => r.error).join('; ')}
        </Alert>
      )}

      {queue.length > 0 && (
        <div className="table-wrap" style={{ marginBottom: '16px' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Size</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((item) => (
                <tr key={item.key}>
                  <td style={{ overflowWrap: 'anywhere' }}>{item.file.name}</td>
                  <td className="muted small">{formatBytes(item.file.size)}</td>
                  <td>
                    {item.status === 'uploading' && (
                      <div>
                        <div
                          aria-label={`Uploading ${item.file.name}`}
                          role="progressbar"
                          aria-valuenow={item.progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          style={{ height: '6px', borderRadius: '999px', background: 'var(--color-border)' }}
                        >
                          <div style={{ width: `${item.progress}%`, height: '100%', borderRadius: '999px', background: 'var(--color-primary)' }} />
                        </div>
                        <span className="muted small">{item.progress}%</span>
                      </div>
                    )}
                    {item.status === 'queued' && <span className="muted small">Queued</span>}
                    {item.status === 'done' && <span className="badge badge-success">Uploaded</span>}
                    {item.status === 'error' && <span className="badge badge-danger" title={item.error}>Failed</span>}
                    {item.status === 'error' && <div className="muted small">{item.error}</div>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {item.status !== 'uploading' && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeItem(item.key)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="field">
        <label htmlFor="upload-name">Name</label>
        <input
          id="upload-name"
          className="input"
          value={fields.name}
          disabled={uploading}
          onChange={(e) => setFields((f) => ({ ...f, name: e.target.value }))}
          placeholder="Left blank, the filename is used"
        />
      </div>

      <div className="field">
        <label htmlFor="upload-alt">Alt text / description</label>
        <textarea
          id="upload-alt"
          className="textarea"
          value={fields.description}
          disabled={uploading}
          onChange={(e) => setFields((f) => ({ ...f, description: e.target.value }))}
          placeholder="What the image shows — used as alt text on the site"
        />
      </div>

      <div className="field">
        <label htmlFor="upload-tags">Tags</label>
        <TagInput
          id="upload-tags"
          value={fields.tags}
          disabled={uploading}
          onChange={(tags) => setFields((f) => ({ ...f, tags }))}
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="upload-source">Source</label>
          <input
            id="upload-source"
            className="input"
            value={fields.source}
            disabled={uploading}
            onChange={(e) => setFields((f) => ({ ...f, source: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="upload-contributor">Contributor</label>
          <input
            id="upload-contributor"
            className="input"
            value={fields.contributor_name}
            disabled={uploading}
            onChange={(e) => setFields((f) => ({ ...f, contributor_name: e.target.value }))}
          />
        </div>
      </div>

      <div className="field field-inline">
        <input
          id="upload-editorial"
          type="checkbox"
          checked={fields.editorial_only}
          disabled={uploading}
          onChange={(e) => setFields((f) => ({ ...f, editorial_only: e.target.checked }))}
        />
        <label htmlFor="upload-editorial">Editorial use only</label>
      </div>

      <p className="muted small">
        These fields apply to every file in the batch; each image stays editable afterwards.
        Thumbnails are generated a few seconds after upload, so new images show a Processing badge first.
      </p>

      <div className="toolbar sticky-actions">
        <button type="button" className="btn btn-primary" onClick={startUpload} disabled={uploading || !pendingCount}>
          {uploading ? 'Uploading…' : `Upload${pendingCount ? ` ${pendingCount} file${pendingCount === 1 ? '' : 's'}` : ''}`}
        </button>
      </div>
    </div>
  );
}
